import * as THREE from 'three'

import ProjectBoardMaterial from '../../Materials/ProjectBoard.js'
import { loadBoardTexture } from '../../Utils/loadBoardTexture.js'
import { createFloorTexture } from '../../Utils/createFloorTexture.js'
import gsap from 'gsap'

export default class Project
{
    constructor(_options)
    {
        // Options
        this.time = _options.time
        this.resources = _options.resources
        this.objects = _options.objects
        this.areas = _options.areas
        this.name = _options.name
        this.geometries = _options.geometries
        this.meshes = _options.meshes
        this.debug = _options.debug
        this.name = _options.name
        this.x = _options.x
        this.y = _options.y
        this.imageSources = _options.imageSources
        this.floorDetails = _options.floorDetails
        this.link = _options.link
        this.distinctions = _options.distinctions

        // Set up
        this.container = new THREE.Object3D()
        this.container.matrixAutoUpdate = false

        this.setBoards()
        this.setFloor()
        this.setLinkArea()
    }

    setBoards()
    {
        // Set up
        this.boards = {}
        this.boards.items = []
        this.boards.xStart = - 5
        this.boards.xInter = 5
        this.boards.y = 5
        this.boards.color = '#8e7161'
        this.boards.threeColor = new THREE.Color(this.boards.color)

        if(this.debug)
        {
            this.debug.addColor(this.boards, 'color').name('boardColor').onChange(() =>
            {
                this.boards.threeColor.set(this.boards.color)
            })
        }

        // Create each board
        let i = 0

        for(const _imageSource of this.imageSources)
        {
            // Set up
            const board = {}
            board.x = this.x + this.boards.xStart + i * this.boards.xInter
            board.y = this.y + this.boards.y

            // Create structure with collision
            this.objects.add({
                base: this.resources.items.projectsBoardStructure.scene,
                collision: this.resources.items.projectsBoardCollision.scene,
                floorShadowTexture: this.resources.items.projectsBoardStructureFloorShadowTexture,
                offset: new THREE.Vector3(board.x, board.y, 0),
                rotation: new THREE.Euler(0, 0, 0),
                duplicated: true,
                mass: 0
            })

            // Plane
            board.planeMesh = this.meshes.boardPlane.clone()
            board.planeMesh.position.x = board.x
            board.planeMesh.position.y = board.y
            board.planeMesh.matrixAutoUpdate = false
            board.planeMesh.updateMatrix()
            board.planeMesh.material = new ProjectBoardMaterial()
            board.planeMesh.material.uniforms.uColor.value = this.boards.threeColor
            board.planeMesh.material.uniforms.uTextureAlpha.value = 0
            this.container.add(board.planeMesh)

            // Image load
            loadBoardTexture(
                _imageSource,
                (texture) =>
                {
                    board.texture = texture
                    board.planeMesh.material.uniforms.uTexture.value = board.texture
                    gsap.to(board.planeMesh.material.uniforms.uTextureAlpha, { value: 1, duration: 1, ease: 'power4.inOut' })

                    // #region agent log
                    fetch('http://127.0.0.1:7823/ingest/d35b8971-5abb-4ced-aa93-3de090899b87',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'0ad91f'},body:JSON.stringify({sessionId:'0ad91f',runId:'project-data-fix',location:'Project.js:setBoards',message:'project texture loaded',data:{name:this.name,source:_imageSource},timestamp:Date.now(),hypothesisId:'L'})}).catch(()=>{});
                    // #endregion
                },
                (error) =>
                {
                    // #region agent log
                    fetch('http://127.0.0.1:7823/ingest/d35b8971-5abb-4ced-aa93-3de090899b87',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'0ad91f'},body:JSON.stringify({sessionId:'0ad91f',runId:'project-data-fix',location:'Project.js:setBoards',message:'project texture failed',data:{name:this.name,source:_imageSource,error:String(error)},timestamp:Date.now(),hypothesisId:'L'})}).catch(()=>{});
                    // #endregion
                },
                { fallbackLabel: this.name },
            )

            // Save
            this.boards.items.push(board)

            i++
        }
    }

    setFloor()
    {
        this.floor = {}
        this.floor.x = 0
        this.floor.y = -2

        this.floor.container = new THREE.Object3D()
        this.floor.container.position.x = this.x + this.floor.x
        this.floor.container.position.y = this.y + this.floor.y
        this.floor.container.matrixAutoUpdate = false
        this.floor.container.updateMatrix()
        this.container.add(this.floor.container)

        this.floor.texture = createFloorTexture({
            title: this.floorDetails.title,
            subtitle: this.floorDetails.subtitle,
            description: this.floorDetails.description,
            meta: this.floorDetails.meta,
        })
        this.floor.texture.magFilter = THREE.NearestFilter
        this.floor.texture.minFilter = THREE.LinearFilter

        this.floor.material = new THREE.MeshBasicMaterial({
            transparent: true,
            depthWrite: false,
            alphaMap: this.floor.texture,
        })

        this.floor.mesh = new THREE.Mesh(this.geometries.floor, this.floor.material)
        this.floor.mesh.matrixAutoUpdate = false
        this.floor.container.add(this.floor.mesh)

        // #region agent log
        fetch('http://127.0.0.1:7823/ingest/d35b8971-5abb-4ced-aa93-3de090899b87',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'0ad91f'},body:JSON.stringify({sessionId:'0ad91f',runId:'project-data-fix',location:'Project.js:setFloor',message:'project floor text created',data:{name:this.name,title:this.floorDetails.title},timestamp:Date.now(),hypothesisId:'M'})}).catch(()=>{});
        // #endregion
    }

    setLinkArea()
    {
        this.linkArea = {}

        this.linkArea.x = 0
        this.linkArea.y = -2

        // Container
        this.linkArea.container = new THREE.Object3D()
        this.linkArea.container.position.x = this.x + this.linkArea.x
        this.linkArea.container.position.y = this.y + this.linkArea.y
        this.linkArea.container.matrixAutoUpdate = false
        this.linkArea.container.updateMatrix()
        this.container.add(this.linkArea.container)

        // Distinctions
        if(this.distinctions)
        {
            for(const _distinction of this.distinctions)
            {
                let base = null
                let collision = null
                let shadowSizeX = null
                let shadowSizeY = null

                switch(_distinction.type)
                {
                    case 'awwwards':
                        base = this.resources.items.projectsDistinctionsAwwwardsBase.scene
                        collision = this.resources.items.projectsDistinctionsAwwwardsCollision.scene
                        shadowSizeX = 1.5
                        shadowSizeY = 1.5
                        break

                    case 'fwa':
                        base = this.resources.items.projectsDistinctionsFWABase.scene
                        collision = this.resources.items.projectsDistinctionsFWACollision.scene
                        shadowSizeX = 2
                        shadowSizeY = 1
                        break

                    case 'cssda':
                        base = this.resources.items.projectsDistinctionsCSSDABase.scene
                        collision = this.resources.items.projectsDistinctionsCSSDACollision.scene
                        shadowSizeX = 1.2
                        shadowSizeY = 1.2
                        break
                }

                this.objects.add({
                    base: base,
                    collision: collision,
                    offset: new THREE.Vector3(this.x + this.linkArea.x + _distinction.x, this.y + this.linkArea.y + _distinction.y, 0),
                    rotation: new THREE.Euler(0, 0, 0),
                    duplicated: true,
                    shadow: { sizeX: shadowSizeX, sizeY: shadowSizeY, offsetZ: - 0.1, alpha: 0.5 },
                    mass: 1.5,
                    soundName: 'woodHit'
                })
            }
        }

        // Area
        this.linkArea.area = this.areas.add({
            position: new THREE.Vector2(this.x + this.link.x, this.y + this.linkArea.y + this.link.y),
            halfExtents: new THREE.Vector2(this.link.halfExtents.x, this.link.halfExtents.y)
        })
        this.linkArea.area.on('interact', () =>
        {
            window.open(this.link.href, '_blank')
        })

        // Area label
        this.linkArea.areaLabel = this.meshes.areaLabel.clone()
        this.linkArea.areaLabel.position.x = this.link.x
        this.linkArea.areaLabel.position.y = this.link.y
        this.linkArea.areaLabel.position.z = 0.001
        this.linkArea.areaLabel.matrixAutoUpdate = false
        this.linkArea.areaLabel.updateMatrix()
        this.linkArea.container.add(this.linkArea.areaLabel)
    }
}
